import type { CSSProperties } from 'react';
import { Link as RouterLink } from 'react-router';

/**
 * Brand mark — the 💨 cloud emoji.
 *
 * Single source of truth for the Poof glyph across every surface:
 * marketing apex (poof.bio), bouncer apex (go.poof.bio), dashboard
 * header, auth pages, favicon. Earlier the dashboard used a custom
 * SVG (two diagonal "wisps"); we consolidated on the emoji for
 * recognizability — the favicon was already on the cloud, and the
 * abstract wisps didn't read as a clear "poof" at glance sizes.
 *
 * Emoji is colored intrinsically (no currentColor styling), so the
 * `tone` prop on <Wordmark> is now a no-op kept for backwards-compat
 * with existing call sites. The drop-shadow gives the glyph subtle
 * presence on dark surfaces without painting it.
 */
export function Mark({
  size = 24,
  className = '',
  style,
}: {
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      className={`inline-flex items-center justify-center leading-none ${className}`}
      style={{
        width: size,
        height: size,
        // Render the emoji at ~70% of the box so it visually fills the
        // requested footprint (emojis ship with built-in padding).
        fontSize: Math.round(size * 0.95),
        filter: 'drop-shadow(0 2px 8px rgba(168, 85, 247, 0.35))',
        ...style,
      }}
      aria-hidden
    >
      💨
    </span>
  );
}

/**
 * Wordmark — Mark + "poof" lockup. The lockup itself is a router-link
 * to `/` (marketing) or `/app` (in-dashboard) — pass `to` to control.
 *
 * Three sizes mapping to common contexts:
 *   sm — dashboard header (22 mark + label 16)
 *   md — auth / onboarding (26 mark + label 18)
 *   lg — marketing hero (36 mark + label 24)
 *
 * The `tone` prop ('gradient' | 'solid') used to drive a colour
 * treatment on the old SVG mark. The emoji is colored intrinsically
 * so the prop is now ignored — kept for backwards-compat with
 * existing call sites; safe to remove in a future cleanup pass.
 */
export function Wordmark({
  to = '/',
  size = 'sm',
  className = '',
}: {
  to?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Legacy prop — no-op for the emoji mark. */
  tone?: 'gradient' | 'solid';
  className?: string;
}) {
  const markSize = size === 'lg' ? 36 : size === 'md' ? 26 : 22;
  const labelSize =
    size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-lg' : 'text-base';
  const gap = size === 'lg' ? 'gap-2.5' : 'gap-2';
  return (
    <RouterLink to={to} className={`inline-flex items-center ${gap} ${className}`}>
      <Mark size={markSize} />
      <span
        className={`${labelSize} font-display font-extrabold tracking-tight text-[var(--color-foreground)]`}
        style={{ letterSpacing: '-0.04em' }}
      >
        poof
      </span>
    </RouterLink>
  );
}
